import { Button, Input, Textarea } from '../components/admin/ui';
import { useEffect, useState } from 'react';
import {
  Plus,
  Trash2,
  Pencil,
  X,
  Loader2,
  MessageSquareText,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { adminApi, CannedResponse } from '../lib/admin-api';

export function CannedResponsesPage() {
  const { profile } = useAuth();
  const [responses, setResponses] = useState<CannedResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formShortCode, setFormShortCode] = useState('');
  const [formTitle, setFormTitle] = useState('');
  const [formContent, setFormContent] = useState('');
  const [formCategory, setFormCategory] = useState('');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    fetchResponses();
  }, [profile?.organization_id]);

  async function fetchResponses() {
    if (!profile?.organization_id) return;
    setLoading(true);
    const data = await adminApi.getCannedResponses(profile.organization_id);
    setResponses(data);
    setLoading(false);
  }

  const resetForm = () => {
    setFormShortCode('');
    setFormTitle('');
    setFormContent('');
    setFormCategory('');
    setFormError('');
    setShowForm(false);
    setEditingId(null);
  };

  const handleEdit = (response: CannedResponse) => {
    setEditingId(response.id);
    setFormShortCode(response.short_code);
    setFormTitle(response.title);
    setFormContent(response.content);
    setFormCategory(response.category || '');
    setFormError('');
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!formShortCode.trim() || !formTitle.trim() || !formContent.trim()) {
      setFormError('Short code, title, and content are required');
      return;
    }

    if (!/^[a-z0-9-_]+$/.test(formShortCode.trim())) {
      setFormError('Short code must be lowercase letters, numbers, hyphens, or underscores');
      return;
    }

    setSaving(true);
    setFormError('');

    if (editingId) {
      const success = await adminApi.updateCannedResponse(editingId, {
        short_code: formShortCode.trim(),
        title: formTitle.trim(),
        content: formContent.trim(),
        category: formCategory.trim() || null,
      });
      if (!success) {
        setFormError('Failed to update. Short code may already exist.');
        setSaving(false);
        return;
      }
    } else {
      if (!profile?.organization_id || !profile?.id) return;
      const result = await adminApi.createCannedResponse(
        profile.organization_id,
        profile.id,
        {
          shortCode: formShortCode.trim(),
          title: formTitle.trim(),
          content: formContent.trim(),
          category: formCategory.trim() || undefined,
        }
      );
      if (!result) {
        setFormError('Failed to create. Short code may already exist.');
        setSaving(false);
        return;
      }
    }

    setSaving(false);
    resetForm();
    fetchResponses();
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this canned response?')) return;
    await adminApi.deleteCannedResponse(id);
    fetchResponses();
  };

  // Group by category
  const categories = [...new Set(responses.map((r) => r.category || 'Uncategorized'))].sort();

  return (
    <div className="support-page">
      <div className="support-page-heading">
        <div>
          <h1 className="support-page-title">Saved replies</h1>
          <p className="text-muted-foreground mt-1">
            Quick reply templates — type /{'{shortcode}'} in the reply composer
          </p>
        </div>
        <Button variant="ghost"
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary transition-colors text-sm font-medium"
        >
          <Plus className="w-4 h-4" />
          New Response
        </Button>
      </div>

      {/* Create/Edit Form */}
      {showForm && (
        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">
              {editingId ? 'Edit Response' : 'New Canned Response'}
            </h3>
            <Button aria-label="Close" size="iconSm" variant="ghost" onClick={resetForm} className="text-muted-foreground hover:text-muted-foreground">
              <X className="w-5 h-5" />
            </Button>
          </div>

          {formError && (
            <div className="mb-4 p-3 bg-destructive/10 border border-destructive/30 rounded-lg text-sm text-error-text">
              {formError}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Short Code
              </label>
              <div className="flex items-center">
                <span className="text-muted-foreground mr-1">/</span>
                <Input
                  type="text"
                  value={formShortCode}
                  onChange={(e) => setFormShortCode(e.target.value.toLowerCase().replace(/[^a-z0-9-_]/g, ''))}
                  placeholder="greeting"
                  className="flex-1 border border-border rounded-md px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-ring focus:border-primary"
                />
              </div>
            </div>
            <div>
              <label htmlFor="cannedresponsespage-1" className="block text-sm font-medium text-foreground mb-1">Title</label>
              <Input id="cannedresponsespage-1"
                type="text"
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
                placeholder="Welcome greeting"
                className="w-full border border-border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-ring focus:border-primary"
              />
            </div>
          </div>

          <div className="mb-4">
            <label htmlFor="cannedresponsespage-2" className="block text-sm font-medium text-foreground mb-1">Content</label>
            <Textarea id="cannedresponsespage-2"
              value={formContent}
              onChange={(e) => setFormContent(e.target.value)}
              placeholder="Hi! Thanks for reaching out. How can I help you today?"
              rows={4}
              className="w-full border border-border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-ring focus:border-primary resize-none"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-foreground mb-1">
              Category <span className="text-muted-foreground font-normal">(optional)</span>
            </label>
            <Input
              type="text"
              value={formCategory}
              onChange={(e) => setFormCategory(e.target.value)}
              placeholder="General"
              className="w-full max-w-xs border border-border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-ring focus:border-primary"
            />
          </div>

          <div className="flex gap-3">
            <Button variant="ghost"
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary disabled:bg-primary transition-colors text-sm font-medium flex items-center gap-2"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {editingId ? 'Update' : 'Create'}
            </Button>
            <Button variant="ghost"
              onClick={resetForm}
              className="px-4 py-2 bg-muted text-foreground rounded-lg hover:bg-border transition-colors text-sm font-medium"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Responses list */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-link" />
        </div>
      ) : responses.length === 0 ? (
        <div className="text-center py-12 bg-card rounded-xl border border-border">
          <MessageSquareText className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">No canned responses yet</p>
          <p className="text-sm text-muted-foreground mt-1">
            Create templates to speed up your replies
          </p>
        </div>
      ) : (
        categories.map((category) => {
          const categoryResponses = responses.filter(
            (r) => (r.category || 'Uncategorized') === category
          );
          if (categoryResponses.length === 0) return null;

          return (
            <div key={category}>
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                {category}
              </h3>
              <div className="space-y-2">
                {categoryResponses.map((response) => (
                  <div
                    key={response.id}
                    className="bg-card rounded-lg border border-border p-4 hover:border-border transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3">
                          <code className="text-sm text-link bg-primary/15 px-2 py-0.5 rounded font-mono">
                            /{response.short_code}
                          </code>
                          <span className="text-sm font-medium text-foreground">
                            {response.title}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            Used {response.usage_count} time{response.usage_count === 1 ? '' : 's'}
                          </span>
                        </div>
                        <p className="mt-2 text-sm text-muted-foreground whitespace-pre-wrap line-clamp-2">
                          {response.content}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 ml-4">
                        <Button aria-label="Edit item" size="iconSm" variant="ghost"
                          onClick={() => handleEdit(response)}
                          className="p-1.5 text-muted-foreground hover:text-link hover:bg-primary/15 rounded transition-colors"
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button aria-label="Delete item" size="iconSm" variant="ghost"
                          onClick={() => handleDelete(response.id)}
                          className="p-1.5 text-muted-foreground hover:text-error-text hover:bg-destructive/10 rounded transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
